/**
 * Blog Detail Component
 * - Displays blog post content and metadata
 * - Shows author information and publication details
 * - Includes blog status and engagement metrics
 */

import React from "react";
import { FaRegCalendarAlt, FaRegUser } from "react-icons/fa";
import { BiCategory } from "react-icons/bi";
import { VscNotebook } from "react-icons/vsc";
import { useDispatch } from "react-redux";
import { publishBlog, unpublishBlog, archiveBlog, unarchiveBlog } from "../../redux/slices/blogsCommunitySlice";
import AvatarBlock from "./Shared/AvatarBlock";
import InfoRow from "./Shared/InfoRow";
import StatusBadge from "./Shared/StatusBadge";
import SectionHeader from "./Shared/SectionHeader";
import { toast } from "react-toastify";

const BlogDetail = ({ data }) => {
  const dispatch = useDispatch();
  const blogId = data.uuid || data.id;

  const title = data.title || "NA";
  const subTitle = data.sub_title || "NA";
  const author = data.author?.name || "NA";
  const submitted = data.published_at
    ? new Date(data.published_at).toISOString().slice(0, 10)
    : "NA";
  const category = data.category || "NA";
  const status = data.status || "NA";
  const content = data.content || "NA";

  const handleStatusChange = async (action) => {
    try {
      console.log(`BlogDetail: Executing action: ${action} for blog ${blogId}`);

      // Call the appropriate API based on the selected action
      if (action === 'publish') {
        await dispatch(publishBlog(blogId)).unwrap();
        console.log('BlogDetail: Blog published successfully');
      } else if (action === 'unpublish') {
        await dispatch(unpublishBlog(blogId)).unwrap();
        console.log('BlogDetail: Blog unpublished successfully');
      } else if (action === 'archive') {
        await dispatch(archiveBlog(blogId)).unwrap();
        console.log('BlogDetail: Blog archived successfully');
      } else if (action === 'unarchive') {
        await dispatch(unarchiveBlog(blogId)).unwrap();
        console.log('BlogDetail: Blog unarchived successfully');
      }

      // Refresh the page after successful API call
      window.location.reload();
    } catch (error) {
      console.error('BlogDetail: Status change failed:', error);
      const errorMessage = error?.response?.data?.meta?.message || 'Failed to change blog status. Please try again.';
      toast.error(errorMessage);
    }
  };

  // All info rows together
  const infoItems = [
    { icon: FaRegUser, label: `Author: ${author}` },
    { icon: VscNotebook, label: `Title: ${title}` },
    { icon: VscNotebook, label: `Sub Title: ${subTitle}` },
    { icon: FaRegUser, label: `Vet Id: ${data.author?.uuid || 'NA'}` },
    { icon: BiCategory, label: `Category: ${category}` },
    { icon: FaRegCalendarAlt, label: `Published: ${submitted}` },
  ];

  // Split into two columns dynamically (left/right)
  const mid = Math.ceil(infoItems.length / 2);
  const leftInfo = infoItems.slice(0, mid);
  const rightInfo = infoItems.slice(mid);

  return (
    <div className="flex flex-col gap-8 mt-6 lg:mt-10">
      {/* Header with Avatar + Status Dropdown */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 lg:gap-8">
        <AvatarBlock title={author} subtitle="Author" />
          <StatusBadge status={status} />
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              onChange={(e) => handleStatusChange(e.target.value)}
              defaultValue=""
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="" disabled>Select Action</option>
              {status === 'draft' && (
                <>
                  <option value="publish">Publish Blog</option>
                  <option value="archive">Archive Blog</option>
                </>
              )}
              {status === 'published' && (
                <>
                  <option value="unpublish">Unpublish Blog</option>
                  <option value="archive">Archive Blog</option>
                </>
              )}
              {status === 'archived' && (
                <option value="unarchive">Unarchive Blog</option>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Info Section in Two Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-8 text-gray-800 text-sm sm:text-base mt-2 pl-4 lg:ml-20">
        {/* Left Column */}
        <div className="flex flex-col gap-3">
          {leftInfo.map((item, idx) => (
            <InfoRow key={idx} icon={item.icon}>
              {item.label}
            </InfoRow>
          ))}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-3">
          {rightInfo.map((item, idx) => (
            <InfoRow key={idx} icon={item.icon}>
              {item.label}
            </InfoRow>
          ))}
        </div>
      </div>

      {/* Content Section */}
      <div>
        <SectionHeader className="mt-2 lg:mt-6">Content</SectionHeader>
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-gray-700 whitespace-pre-wrap">{content}</p>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
